import { createI18n } from 'vue-i18n'

export type SupportedLocale = 'en' | 'zh-CN'

const STORAGE_KEY = 'app.locale'

/**
 * 从浏览器偏好推断默认语言：
 * - navigator.language 匹配 zh → zh-CN
 * - 其他 → en
 */
function detectLocale(): SupportedLocale {
  if (typeof navigator !== 'undefined') {
    const lang = navigator.language?.toLowerCase() ?? ''
    if (lang.startsWith('zh')) return 'zh-CN'
  }
  return 'en'
}

/**
 * 从 localStorage 读取用户手动选择的语言（覆盖浏览器默认值）
 */
export function getInitialLocale(): SupportedLocale {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'en' || saved === 'zh-CN') return saved
  }
  return detectLocale()
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: getInitialLocale(),
  fallbackLocale: 'en',
  // 初始不预注册任何 locale 的空消息
  // 让 setLocale 通过 availableLocales 检查可靠工作
  messages: {},
})

/**
 * 加载语言文件（仅在该 locale 还未加载时）
 */
export async function loadMessages(locale: SupportedLocale) {
  // 检查该 locale 是否真的加载了消息（而非仅有空对象）
  const messages = (i18n.global.messages.value as Record<string, Record<string, unknown>>)[locale]
  if (messages && Object.keys(messages).length > 0) return
  const mod = await import(`./locales/${locale}.json`)
  i18n.global.setLocaleMessage(locale, mod.default)
}

/**
 * 切换语言并持久化
 */
export async function setLocale(locale: SupportedLocale) {
  await loadMessages(locale)
  i18n.global.locale.value = locale
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, locale)
  }
  document.documentElement.lang = locale
}
